import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ContactForm = () => {
  const navigate = useNavigate();

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: ""
  });

  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => {
    const e = {};
    if (!values.firstName.trim()) e.firstName = "First name is required";
    if (!values.lastName.trim()) e.lastName = "Last name is required";
    if (!values.email.trim()) e.email = "Email is required";
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      e.email = "Enter a valid email address";
    if (!values.subject.trim()) e.subject = "Subject is required";
    if (!values.message.trim()) e.message = "Message is required";
    if (values.message.trim() && values.message.trim().length < 10)
      e.message = "Message must be at least 10 characters";
    return e;
  }, [values]);

  const isValid = Object.keys(errors).length === 0;

  const setField = (key) => (e) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const markTouched = (key) => () => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const showError = (key) => touched[key] && errors[key];

  const submit = (e) => {
    e.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      subject: true,
      message: true
    });
    if (!isValid) return;

    setSubmitting(true);

    window.sessionStorage.setItem(
      "qz_contact_submission",
      JSON.stringify({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim()
      })
    );

    setTimeout(() => {
      setSubmitting(false);
      navigate("/contact/success");
    }, 600);
  };

  const fieldClass = (key) =>
    `w-full rounded-xl border px-4 py-3 text-sm outline-none transition bg-white ${
      showError(key)
        ? "border-red-300 focus:border-red-400"
        : "border-[color:var(--border)] focus:border-[color:var(--brand)]"
    }`;

  return (
    <section
      aria-labelledby="contact-form-title"
      className="relative overflow-hidden rounded-2xl border border-[color:var(--border)] bg-white shadow-sm"
    >
      <div className="absolute -top-24 -right-28 h-72 w-72 rounded-full bg-[color:var(--brand-accent)]/12 blur-3xl" />
      <div className="absolute -bottom-28 -left-28 h-80 w-80 rounded-full bg-[color:var(--brand)]/10 blur-3xl" />

      <div className="relative p-8 sm:p-10">
        <header className="max-w-2xl">
          <p className="text-sm font-semibold text-[color:var(--brand)]">
            Contact
          </p>
          <h2
            id="contact-form-title"
            className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--navy)]"
          >
            Send us a message
          </h2>
          <p className="mt-3 text-sm text-[color:var(--muted)]">
            Tell us a bit about your needs and we’ll respond with next steps.
          </p>
        </header>

        <form onSubmit={submit} className="mt-8 grid gap-6" noValidate>
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="text-sm font-semibold text-[color:var(--navy)]">
                First name
              </label>
              <input
                id="firstName"
                value={values.firstName}
                onChange={setField("firstName")}
                onBlur={markTouched("firstName")}
                className={fieldClass("firstName")}
                autoComplete="given-name"
                aria-invalid={showError("firstName")}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="text-sm font-semibold text-[color:var(--navy)]">
                Last name
              </label>
              <input
                id="lastName"
                value={values.lastName}
                onChange={setField("lastName")}
                onBlur={markTouched("lastName")}
                className={fieldClass("lastName")}
                autoComplete="family-name"
                aria-invalid={showError("lastName")}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="text-sm font-semibold text-[color:var(--navy)]">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={values.email}
              onChange={setField("email")}
              onBlur={markTouched("email")}
              className={fieldClass("email")}
              autoComplete="email"
              aria-invalid={showError("email")}
            />
          </div>

          <div>
            <label htmlFor="subject" className="text-sm font-semibold text-[color:var(--navy)]">
              Subject
            </label>
            <input
              id="subject"
              value={values.subject}
              onChange={setField("subject")}
              onBlur={markTouched("subject")}
              className={fieldClass("subject")}
              aria-invalid={showError("subject")}
            />
          </div>

          <div>
            <label htmlFor="message" className="text-sm font-semibold text-[color:var(--navy)]">
              Message
            </label>
            <textarea
              id="message"
              value={values.message}
              onChange={setField("message")}
              onBlur={markTouched("message")}
              className={`${fieldClass("message")} min-h-[160px] resize-none`}
              aria-invalid={showError("message")}
            />
          </div>

            <div className="pt-2">
                    <button
                        type="submit"
                        disabled={submitting}
                        className={`w-full inline-flex items-center justify-center rounded-xl px-10 py-4 text-sm font-semibold transition shadow-lg ${
                        submitting
                            ? "bg-[color:var(--brand)]/35 text-white/70 cursor-not-allowed"
                            : "bg-[color:var(--brand-accent)] text-[color:var(--navy)] hover:brightness-95"
                        }`}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </button>
            </div>

        </form>
      </div>
    </section>
  );
};

export default ContactForm;
